import { Flex, Box, Icon, Center, Spinner, Tooltip } from '@chakra-ui/react';
import { Select } from '@/design-system/select';
import { XIcon } from '@heroicons/react/solid';
import { useField } from 'formik';
import {
  SelectParentChildRootOption,
  DepartmentMultiValue,
} from '@/components/formBuilder/SelectComponents';

import { FilterType } from './filter-config/types';
import {
  FILTER_CONFIG,
  useFilterConfig,
} from './filter-config/use-filter-config';
import { KText } from '@/ui-library/components/text';
import { KInput } from '@/ui-library/components/atoms/KInput/KInput';
import { Trash01 } from '@kula-ai/icons/outline/Trash01';
import { useKFlags } from '@/hooks/use-k-flags';
import { InfoCircle } from '@kula-ai/icons/outline/InfoCircle';
import { components as selectComponents } from 'react-select';
import {
  filterConditions,
  getDefaultFilterCondition,
  isNumericFilter,
  getNumericFilterDisplayValue,
  buildNumericFilterValues,
} from './filterItemConstants';
import { FILTER_COMPARISON_CONDITIONS } from '@/components/filters_revamp/utils/constants';
import { useMemo, useCallback } from 'react';
import type { ArrayHelpers } from 'formik';

const CustomOption = (props: {
  data?: { value?: string; label?: string };
  [key: string]: unknown;
}) => {
  const { data } = props;
  const filterConfig = FILTER_CONFIG[data?.value];
  const hasTooltip = filterConfig?.tooltip;

  return (
    <selectComponents.Option {...props}>
      <Flex alignItems="center" gap="8px" width="100%">
        <KText size="sm-regular">{data.label}</KText>
        {hasTooltip && (
          <Tooltip
            variant="default"
            label={filterConfig.tooltip}
            placement="top"
            shouldWrapChildren
            hasArrow
          >
            <Icon
              cursor="pointer"
              w="16px"
              h="16px"
              as={InfoCircle}
              color="gray.500"
              position="relative"
              bottom="0.5px"
            />
          </Tooltip>
        )}
      </Flex>
    </selectComponents.Option>
  );
};

interface FilterRowProps {
  filter: FilterType;
  idx: number;
  arrayHelpers: ArrayHelpers;
  options: string[];
  reportType?: string;
}

export const FilterRow = ({
  arrayHelpers = {},
  idx = 0,
  options = [],
  filter,
  reportType,
}: FilterRowProps) => {
  const [{ value }, , { setValue }] = useField(`filters[${idx}]`);

  const { feReportsV3 } = useKFlags();

  const {
    options: filterOptions,
    isLoading,
    valueField,
    labelField,
    components,
    tagKey,
    tooltip,
    newStyles,
    operatorType,
    excludeFilterCondition = false,
    showToggle,
    isMulti = true,
  } = useFilterConfig(value?.filter_by?.value, { reportType: reportType });

  const filterConditionsOptions = useMemo(() => {
    return isNumericFilter(value?.filter_by?.value, operatorType)
      ? FILTER_COMPARISON_CONDITIONS
      : filterConditions;
  }, [operatorType, value?.filter_by?.value]);

  const resultFilterOptions =
    filter?.filter_by?.value === 'department'
      ? filterOptions?.map((option) => {
          return {
            ...option,
            parent_id:
              option?.parent_department_id > 0
                ? option?.parent_department_id
                : null,
          };
        })
      : filterOptions;

  const numericDisplayValue = useMemo(
    () =>
      operatorType === 'number'
        ? getNumericFilterDisplayValue(value?.filter_by_values?.[0], valueField)
        : '',
    [operatorType, value?.filter_by_values, valueField]
  );

  const handleNumericChange = useCallback(
    (raw: string) => {
      const filter_by_values = buildNumericFilterValues(
        raw,
        valueField,
        labelField,
        filterOptions as { id: number; label: string }[]
      );
      setValue({ ...value, filter_by_values });
    },
    [value, valueField, labelField, filterOptions, setValue]
  );

  const customStyles = [
    ...(newStyles || []),
    {
      type: 'menuPortal',
      newStyles: {
        zIndex: 1000,
      },
    },
  ];

  const CustomLoadingMessage = () => {
    return (
      <Center py="20px">
        <Spinner color="primary-800" size="md" thickness="3px" />
      </Center>
    );
  };

  return (
    <Flex alignItems="baseline" mt="12px" role="group">
      <KText variant="heading_4" mt="8px" fontWeight={600} width="30px">
        {idx === 0 ? 'By' : 'And'}
      </KText>
      <Box width="230px" ml="12px">
        <Select
          options={options.map((key) => {
            return {
              value: key,
              label: FILTER_CONFIG[key].name,
              tagKey: FILTER_CONFIG[key].tagKey,
              tooltip: FILTER_CONFIG[key].tooltip,
            };
          })}
          value={value?.filter_by}
          menuPortalTarget={document.body}
          newStyles={customStyles}
          onChange={(filter_by) => {
            const filter_condition = getDefaultFilterCondition(
              filter_by?.value
            );
            setValue({
              ...value,
              filter_by,
              filter_condition,
              filter_by_values: [],
            });
          }}
          components={{ Option: CustomOption }}
        />
      </Box>
      {!excludeFilterCondition && (
        <Box width="100px" ml="12px">
          <Select
            options={filterConditionsOptions}
            value={value?.filter_condition}
            menuPortalTarget={document.body}
            newStyles={customStyles}
            onChange={(filter_condition) =>
              setValue({ ...value, filter_condition })
            }
          />
        </Box>
      )}
      <Box flex="1" ml="12px">
        {operatorType === 'number' ? (
          <KInput
            type="number"
            fieldType="number"
            h="38px !important"
            min={1}
            value={numericDisplayValue}
            placeholder="Enter value"
            onChange={(e) => handleNumericChange(e.target.value)}
          />
        ) : (
          <Select
            isMulti={isMulti}
            isClearable={false}
            options={resultFilterOptions ?? []}
            value={
              isMulti
                ? value?.filter_by_values
                : (value?.filter_by_values?.[0] ?? null)
            }
            onChange={(filter_by_values) => {
              const normalized = Array.isArray(filter_by_values)
                ? filter_by_values
                : filter_by_values !== undefined && filter_by_values !== null
                  ? [filter_by_values]
                  : [];
              setValue({ ...value, filter_by_values: normalized });
            }}
            newStyles={customStyles}
            menuPlacement="auto"
            menuPortalTarget={document.body}
            isLoading={isLoading}
            loadingMessage={() => <CustomLoadingMessage />}
            isDisabled={isLoading}
            getOptionLabel={(option) => option?.[labelField]}
            getOptionValue={(option) => option?.[valueField]}
            components={
              filter?.filter_by?.value === 'department'
                ? {
                    Menu: SelectParentChildRootOption,
                    MultiValue: DepartmentMultiValue,
                  }
                : { ...components }
            }
            extraProps={{
              valueKey: valueField,
              labelKey: labelField,
              tagKey,
              tooltip,
              ...(showToggle !== undefined && { showToggle }),
            }}
          />
        )}
      </Box>
      <Box
        width="32px"
        {...(feReportsV3 && {
          ml: '8px',
          w: 'unset',
          p: '6px',
          _hover: { bg: 'secondary-50', borderRadius: '6px' },
        })}
      >
        {idx !== 0 && (
          <Icon
            {...(feReportsV3
              ? {
                  size: '20px',
                }
              : {
                  display: 'none',
                  _groupHover: { display: 'block' },
                  ml: '8px',
                })}
            cursor="pointer"
            as={feReportsV3 ? Trash01 : XIcon}
            onClick={() => {
              arrayHelpers.remove(idx);
            }}
          />
        )}
      </Box>
    </Flex>
  );
};
